import { AccessControlListDc } from "@evergis/api";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { reset, updateAcl } from "../../ducks/acl/actions";
import { getAcl } from "../../ducks/acl/selectors";
import { PublicAccessUpdate } from "../../ducks/acl/types";
import { useAction, useActionWithArgs } from "../useAction";
import { useToggle } from "../useToggle";
import { useTrigger } from "../useTrigger";
import { isPublicAccess } from "./publicAccess";

const initialUpdates: PublicAccessUpdate = {
  layers: [],
  tables: [],
};

const DEFAULT_PUBLIC_ACCESS = false;

export const useAclUpdate = (aclUpdates: PublicAccessUpdate) => {
  const state = useSelector(getAcl);
  const update = useActionWithArgs(updateAcl, aclUpdates);
  const dispatchResetAcl = useAction(reset);

  useTrigger(!state.loading && !!state.data, dispatchResetAcl);

  return [update, state] as const;
};

type AccessControlListUpdate = [
  { publicAccess: boolean; publicAccessUpdates: PublicAccessUpdate },
  (update: PublicAccessUpdate) => void,
];

export const useAccessControlList = (acl?: AccessControlListDc): AccessControlListUpdate => {
  const isPublic = isPublicAccess(acl);
  const [publicAccess, togglePublicAccess] = useToggle(isPublic || DEFAULT_PUBLIC_ACCESS);
  const [publicAccessUpdates, setUpdatesState] = useState(initialUpdates);

  const setPublicAccessUpdates = useCallback(
    (updates: PublicAccessUpdate) => {
      setUpdatesState(updates);
      togglePublicAccess();
    },
    [togglePublicAccess],
  );

  useEffect(() => {
    const aclChanged = (isPublic && !publicAccess) || (!isPublic && publicAccess);

    if (aclChanged) {
      togglePublicAccess();
    }
  }, [acl]);

  return [{ publicAccess, publicAccessUpdates }, setPublicAccessUpdates];
};
